import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  cancelCaseOkQueueGroup,
  fetchCaseOkQueue,
  updateCaseOkQueueItem,
  type CaseOkQueueItem,
} from "../../store/admin/caseOkQueueSlice";
import { useToast } from "../utilities/Toast";

const PAGE_SIZE = 50;
const POLL_MS = 10_000;

const QUEUE_CONTROL_USER_ROLES = ["Super Admin", "Admin", "Operations"];
const QUEUE_CONTROL_EMP_ROLES = ["CRM", "Admin", "Executive", "Super Admin"];

const formatRemaining = (ms: number) => {
  if (ms <= 0) return "Due now";
  const totalSec = Math.floor(ms / 1000);
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) {
    return `${hours}h ${String(mins).padStart(2, "0")}m ${String(secs).padStart(2, "0")}s`;
  }
  return `${mins}m ${String(secs).padStart(2, "0")}s`;
};

const formatSendAt = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const RemainingTimer = ({
  item,
  canControl,
  acting,
  cancelLabel,
  onCancel,
}: {
  item: CaseOkQueueItem;
  canControl: boolean;
  acting: boolean;
  cancelLabel?: string;
  onCancel: (item: CaseOkQueueItem) => void;
}) => {
  const [now, setNow] = useState(Date.now());
  const isPending = item.status === "pending";

  useEffect(() => {
    if (!isPending) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isPending]);

  if (!isPending) return <span className="text-gray-400">—</span>;

  const remaining = new Date(item.send_at).getTime() - now;

  return (
    <div className="flex flex-col items-start gap-1.5">
      <span className="text-xs font-semibold tabular-nums text-sky-800">
        {formatRemaining(remaining)}
      </span>
      {canControl ? (
        <button
          type="button"
          disabled={acting}
          onClick={() => onCancel(item)}
          className="px-2 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 text-[11px] disabled:opacity-50"
        >
          {acting ? "Saving…" : cancelLabel || "Cancel"}
        </button>
      ) : null}
    </div>
  );
};

type QueueSection = {
  key: string;
  isGroup: boolean;
  items: CaseOkQueueItem[];
};

const groupQueueItems = (items: CaseOkQueueItem[]): QueueSection[] => {
  const sections = new Map<string, CaseOkQueueItem[]>();
  const order: string[] = [];

  for (const item of items) {
    const key =
      item.group_id && (item.group_size || 1) >= 2
        ? item.group_id
        : `solo-${item.id}`;
    if (!sections.has(key)) {
      sections.set(key, []);
      order.push(key);
    }
    sections.get(key)!.push(item);
  }

  return order.map((key) => {
    const sectionItems = sections.get(key)!;
    return {
      key,
      isGroup: !key.startsWith("solo-"),
      items: sectionItems,
    };
  });
};

const uniqueValues = (values: string[]) =>
  [...new Set(values.map((value) => value.trim()).filter(Boolean))];

type CaseOkQueueModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CaseOkQueueModal = ({ isOpen, onClose }: CaseOkQueueModalProps) => {
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();
  const { items, totalCount, loading, actingId, actingGroupId, error } =
    useAppSelector((state) => state.caseOkQueue);
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);

  const canControlQueue =
    QUEUE_CONTROL_USER_ROLES.includes(user?.role || "") ||
    QUEUE_CONTROL_EMP_ROLES.includes(employee?.role || "");

  const actedBy =
    user?.role === "Super Admin"
      ? user?.user_name || "Super Admin"
      : employee?.emp_name || user?.user_name || "";

  const [status, setStatus] = useState("pending");
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const sections = useMemo(() => groupQueueItems(items), [items]);

  const loadQueue = useCallback(() => {
    dispatch(
      fetchCaseOkQueue({
        page: 1,
        limit: PAGE_SIZE,
        status,
        search,
      }),
    );
  }, [dispatch, status, search]);

  useEffect(() => {
    if (!isOpen) return;
    loadQueue();
  }, [isOpen, loadQueue]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setInterval(loadQueue, POLL_MS);
    return () => window.clearInterval(timer);
  }, [isOpen, loadQueue]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleCancel = async (item: CaseOkQueueItem) => {
    const result = await dispatch(
      updateCaseOkQueueItem({ id: item.id, action: "cancel", actedBy }),
    );
    if (updateCaseOkQueueItem.fulfilled.match(result)) {
      showToast(
        `Cancelled ${item.case_id}. Send it from the Case OK page.`,
        "success",
      );
      loadQueue();
    } else {
      showToast((result.payload as string) || "Could not cancel", "error");
    }
  };

  const handleCancelGroup = async (section: QueueSection) => {
    const lead = section.items[0];
    if (!lead) return;
    const result = await dispatch(
      cancelCaseOkQueueGroup({ id: lead.id, actedBy }),
    );
    if (cancelCaseOkQueueGroup.fulfilled.match(result)) {
      showToast(
        `Cancelled ${section.items.length} grouped ${lead.lab_token || "lab"} cases. Send them from the Case OK page.`,
        "success",
      );
      loadQueue();
    } else {
      showToast((result.payload as string) || "Could not cancel group", "error");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Case OK Queue
            </h3>
            <p className="text-xs text-gray-500">
              Auto-send 30 min after QC1. Same-lab cases QC1&apos;d within 15
              min send as one email. Cancel to send manually from Case OK.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded border text-sm text-gray-600 hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search case ID…"
            className="border rounded px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-sky-400"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm"
          >
            <option value="pending">Pending</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
            <option value="skipped">Skipped</option>
            <option value="all">All</option>
          </select>
          <span className="text-xs text-gray-500 ml-auto">
            {totalCount} {status === "all" ? "" : status} item
            {totalCount === 1 ? "" : "s"}
          </span>
        </div>

        <div className="overflow-auto px-5 py-3">
          {loading && items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Loading…</div>
          ) : error && items.length === 0 ? (
            <div className="text-center py-8 text-red-500">
              Couldn’t load the queue.
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No emails in this queue.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                  <th className="py-2 pr-3">Case ID</th>
                  <th className="py-2 pr-3">Patient/Job</th>
                  <th className="py-2 pr-3">Service</th>
                  <th className="py-2 pr-3">QC1 By</th>
                  <th className="py-2 pr-3">Send at</th>
                  <th className="py-2 pr-3">Time left</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => {
                  if (!section.isGroup) {
                    const item = section.items[0];
                    return (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="py-2.5 pr-3 font-medium">
                          {item.case_id}
                        </td>
                        <td className="py-2.5 pr-3">
                          {item.case_owner || "—"}
                        </td>
                        <td className="py-2.5 pr-3">
                          {item.service_type === "filledOrder"
                            ? "filledOrder"
                            : item.service_type || "—"}
                        </td>
                        <td className="py-2.5 pr-3">{item.qc1_by || "—"}</td>
                        <td className="py-2.5 pr-3">
                          {formatSendAt(item.send_at)}
                        </td>
                        <td className="py-2.5 pr-3">
                          <RemainingTimer
                            item={item}
                            canControl={canControlQueue}
                            acting={actingId === item.id}
                            onCancel={(row) => void handleCancel(row)}
                          />
                        </td>
                        <td className="py-2.5 capitalize">
                          {item.status}
                          {item.error_message ? (
                            <div className="text-[11px] text-gray-500 mt-0.5 max-w-[200px]">
                              {item.error_message}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  }

                  const lead = section.items[0];
                  const expanded = expandedGroups.has(section.key);
                  const lab = lead.lab_token || lead.case_id.slice(0, 2);
                  const groupCount = Math.max(
                    section.items.length,
                    lead.group_size || 0,
                  );
                  const owners = uniqueValues(
                    section.items.map((item) => item.case_owner),
                  );
                  const services = uniqueValues(
                    section.items.map((item) =>
                      item.service_type === "filledOrder"
                        ? "filledOrder"
                        : item.service_type || "",
                    ),
                  );
                  const qc1Names = uniqueValues(
                    section.items.map((item) => item.qc1_by),
                  );
                  const isActingGroup =
                    actingGroupId === lead.id ||
                    section.items.some((item) => actingGroupId === item.id);

                  return (
                    <FragmentGroup
                      key={section.key}
                      section={section}
                      lead={lead}
                      lab={lab}
                      groupCount={groupCount}
                      expanded={expanded}
                      owners={owners}
                      services={services}
                      qc1Names={qc1Names}
                      canControlQueue={canControlQueue}
                      actingId={actingId}
                      isActingGroup={isActingGroup}
                      onToggle={() => toggleGroup(section.key)}
                      onCancelItem={(row) => void handleCancel(row)}
                      onCancelGroup={() => void handleCancelGroup(section)}
                    />
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const FragmentGroup = ({
  section,
  lead,
  lab,
  groupCount,
  expanded,
  owners,
  services,
  qc1Names,
  canControlQueue,
  actingId,
  isActingGroup,
  onToggle,
  onCancelItem,
  onCancelGroup,
}: {
  section: QueueSection;
  lead: CaseOkQueueItem;
  lab: string;
  groupCount: number;
  expanded: boolean;
  owners: string[];
  services: string[];
  qc1Names: string[];
  canControlQueue: boolean;
  actingId: number | null;
  isActingGroup: boolean;
  onToggle: () => void;
  onCancelItem: (item: CaseOkQueueItem) => void;
  onCancelGroup: () => void;
}) => (
  <>
    <tr className="border-b bg-sky-50/70">
      <td className="py-2.5 pr-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-start gap-2 text-left"
        >
          <span className="mt-0.5 text-[11px] text-sky-700">
            {expanded ? "▼" : "▶"}
          </span>
          <span>
            <span className="font-semibold text-sky-900">
              Lab {lab} · {groupCount} case{groupCount === 1 ? "" : "s"}
            </span>
            <span className="block text-[11px] text-sky-700 mt-0.5">
              One grouped email · QC1 within 15 min
            </span>
          </span>
        </button>
      </td>
      <td className="py-2.5 pr-3 text-gray-600">
        {owners.length ? owners.slice(0, 2).join(", ") : "—"}
        {owners.length > 2 ? ` +${owners.length - 2}` : ""}
      </td>
      <td className="py-2.5 pr-3">{services.join(", ") || "—"}</td>
      <td className="py-2.5 pr-3">{qc1Names.join(", ") || "—"}</td>
      <td className="py-2.5 pr-3">{formatSendAt(lead.send_at)}</td>
      <td className="py-2.5 pr-3">
        <RemainingTimer
          item={lead}
          canControl={canControlQueue}
          acting={isActingGroup}
          cancelLabel="Cancel group"
          onCancel={() => onCancelGroup()}
        />
      </td>
      <td className="py-2.5 capitalize">
        {lead.status}
        <div className="text-[11px] text-sky-700 mt-0.5">Grouped</div>
      </td>
    </tr>
    {expanded
      ? section.items.map((item) => (
          <tr key={item.id} className="border-b last:border-0 bg-sky-50/30">
            <td className="py-2 pr-3 pl-8 font-medium">{item.case_id}</td>
            <td className="py-2 pr-3">{item.case_owner || "—"}</td>
            <td className="py-2 pr-3">
              {item.service_type === "filledOrder"
                ? "filledOrder"
                : item.service_type || "—"}
            </td>
            <td className="py-2 pr-3">{item.qc1_by || "—"}</td>
            <td className="py-2 pr-3">{formatSendAt(item.send_at)}</td>
            <td className="py-2 pr-3">
              <RemainingTimer
                item={item}
                canControl={canControlQueue}
                acting={actingId === item.id}
                cancelLabel="Cancel one"
                onCancel={onCancelItem}
              />
            </td>
            <td className="py-2 capitalize">
              {item.status}
              {item.error_message ? (
                <div className="text-[11px] text-gray-500 mt-0.5 max-w-[200px]">
                  {item.error_message}
                </div>
              ) : null}
            </td>
          </tr>
        ))
      : null}
  </>
);

export default CaseOkQueueModal;
